import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environment/environment';
import { map, Observable, Subject } from 'rxjs';
import { AuditStatus } from 'shared/enums';
import { BaseApiService } from 'shared/services/base-api.service';
import { CONTROLLERS } from 'shared/services/controllers';
import { ApiResponse, AuditFilters, IYardAudit, IYardAuditUpdate } from 'shared/types';

@Injectable({
  providedIn: 'root',
})
export class YardAuditApiService extends BaseApiService<
  ApiResponse<{ items: IYardAudit[] }>,
  ApiResponse<IYardAudit>,
  ApiResponse<{ id: string }>
> {
  public quickSearch$: Subject<string> = new Subject<string>();
  public refresh$: Subject<void> = new Subject<void>();
  public activeTab: AuditStatus | 'onsite' = 'onsite';

  constructor(public override http: HttpClient) {
    super(http, environment.yardOpsApiBaseUrl);
  }

  public controllerName(): string {
    return CONTROLLERS.yardAudits;
  }

  public override update(id: string, entity: IYardAuditUpdate): Observable<ApiResponse<{ id: string }>> {
    const headers = new HttpHeaders().set(this.trackerKeyHeader, `update/${this.controllerName()}/${id}`);

    return this.http.put<ApiResponse<{ id: string }>>(`${this.api}/api/trailers/${id}/yard-audit`, entity, {
      headers: headers,
    });
  }

  public getAuditsBySiteAndStatus(
    filters?: AuditFilters,
  ): Observable<{ data: IYardAudit[]; sums: Record<string, number> }> {
    let params = new HttpParams();
    if (filters) {
      params = this.processFilters(filters, params);
    }
    return this.http.get<ApiResponse<{ items: IYardAudit[] }>>(`${this.api}/${this.controllerName()}`, { params }).pipe(
      map((response) => {
        const filteredData = this.activeTab
          ? response.value.items.filter((item) => {
              const statusMatch = item.auditStatus.toLowerCase() === this.activeTab.toLowerCase();
              if (this.activeTab === AuditStatus.AUDITED) {
                return (
                  statusMatch ||
                  item.auditStatus.toLowerCase() === AuditStatus.PRODUCT_PROTECTION_CONFIRMED.toLowerCase() ||
                  item.auditStatus.toLowerCase() === AuditStatus.PRODUCT_PROTECTION.toLowerCase()
                );
              }

              return statusMatch;
            })
          : response.value.items;

        const sumsByStatus = this.getSumsByStatus(response.value.items);

        return {
          data: filteredData,
          sums: sumsByStatus,
        };
      }),
    );
  }

  private getSumsByStatus(audits: IYardAudit[]): Record<string, number> {
    const statusCounts: Record<string, number> = {
      [AuditStatus.UNAVAILABLE]: 0,
      [AuditStatus.UNAUDITED]: 0,
      [AuditStatus.AUDITED]: 0,
    };

    audits.forEach((audit) => {
      const auditedStatus = audit.auditStatus.toLowerCase() === AuditStatus.AUDITED.toLowerCase();
      const ppConfirmedStatus =
        audit.auditStatus.toLowerCase() === AuditStatus.PRODUCT_PROTECTION_CONFIRMED.toLowerCase() ||
        audit.auditStatus.toLowerCase() === AuditStatus.PRODUCT_PROTECTION.toLowerCase();
      if (auditedStatus || ppConfirmedStatus) {
        statusCounts[AuditStatus.AUDITED.toLowerCase()]++;
      } else {
        statusCounts[audit.auditStatus.toLowerCase()]++;
      }
    });

    statusCounts['onsite'] = audits.length;
    return statusCounts;
  }

  private processFilters(filters: AuditFilters, params: HttpParams): HttpParams {
    if (!filters) return params;

    Object.entries(filters).forEach(([key, value]) => {
      let values = [];
      if (value) {
        if (Array.isArray(value)) {
          values = value;
        } else if (value && typeof value === 'object') {
          values = Object.values(value);
        } else {
          values = [value];
        }

        values
          .filter((x) => x !== null && x !== undefined && x !== '')
          .forEach((value) => {
            params = params.append(key, value as string);
          });
      }
    });
    return params;
  }
}
